import { Injectable } from '@angular/core';

import {
  DataService as CoreDataService,
  IPersistable,
  PersistenceService,
  PersistMode,
  QueryColumn,
  QueryColumnSortOrder,
} from '@barthauer/abc/core/data';

import { GUID } from '@barthauer/abc/core';
import { Observable, of, Subscription } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { FavoriteDataSource } from './favorite-dataSource';
import { Box, BoxDetailViewModel } from './Model/box.viewModel';
import { Address, Audit, BibCategory, BibMeasure, BibQuestion, BibResponse, Library } from '@barthauer/custom/xmt-models';
import { ObjectTypeNames } from '../../favorite-list/favorite-list.component';
import { FavoriteAction } from '../../../models/enumeration-list.model';
import dxDataGrid, { Column, ColumnButtonClickEvent, Row } from 'devextreme/ui/data_grid';
import { Items, ItemsArray } from '../../../models/organizational-structure.model';
import { DataService, ObjectKeys } from '../../../services/data.service';
@Injectable({
  providedIn: 'root',
})
export class FavoritesService {
  public allFavorites: FavoriteDataSource;
  public appObjectTypesKeys: string[] = [];

  private boxIdFavoriten: string;

  constructor(
    private readonly persistenceService: PersistenceService,
    private readonly coreDataService: CoreDataService,
    private readonly dataService: DataService) {}

  public getDataSource(): Observable<FavoriteDataSource> {
    if (this.allFavorites) {
      return of(this.allFavorites);
    } else {
      return this.getFavorites().pipe(
        first(),
        map((data) => {
          this.allFavorites = new FavoriteDataSource(data);
          for (const key in this.allFavorites.favoritesByObjectType) {
            if (
              Object.prototype.hasOwnProperty.call(this.allFavorites.favoritesByObjectType, key) &&
              this.appObjectTypesKeys.includes(key.toString())
            ) {
              const boxDetailViewModel: BoxDetailViewModel[] = this.allFavorites.favoritesByObjectType[key];
              let id: string = '( ';
              boxDetailViewModel.forEach((favorite) => {
                id = id + " '" + favorite.ParentId + "' ,";
              });
              id = id.slice(0, -1) + ')';
              this.getDisplayText(key, id);
            }
          }

          return this.allFavorites;
        })
      );
    }
  }

  private getFavorites(): Observable<BoxDetailViewModel[]> {
    const queryColumns: Array<QueryColumn> = [
      this.coreDataService.createQueryColumn('ParentObjectType', 'ParentObjectType'),
      this.coreDataService.createQueryColumn('ParentId', 'ParentId'),
      this.coreDataService.createQueryColumn('Id', 'Id'),
      this.coreDataService.createQueryColumn('gebId', 'gebId'),
    ];
    const oPath = "Box.ParentId = '" + localStorage.getItem('profileId') + "' AND Box.Designation = 'Favoriten'";
    return this.dataService.readObjects(ObjectKeys.BOXDETAIL,queryColumns,oPath);
  }

  private getDisplayText(objectType: string, ids: string): Subscription {
    return this._getDisplayText(objectType, ids)
      .pipe(first())
      .subscribe((data) => {
        this.allFavorites?.setDisplayText(objectType, data);
      });
  }

  private _getDisplayText(objectType: string, ids: string): Observable<BoxDetailViewModel> {
    const queryColumns: Array<QueryColumn> = [
      this.coreDataService.createQueryColumn('Id', 'Id'),
      this.coreDataService.createQueryColumn('ref:Id', 'DisplayText', QueryColumnSortOrder.Ascending),
    ];
    const oPath = 'Id IN ' + ids;
    return this.dataService.readObjects(objectType,queryColumns,oPath);
  }

  private getBoxId(): Observable<string> {
    return this._getBoxId().pipe(
      first(),
      map((data) => {
        if (data && data.length > 0) {
          this.boxIdFavoriten = data[0]?.Id;

          return this.boxIdFavoriten;
        } else {
          return this.persistBoxFavorite(GUID.newGUID());
        }
      })
    );
  }

  private _getBoxId(): Observable<Pick<Box,'Id'>[]> {
    const queryColumns: Array<QueryColumn> = [this.coreDataService.createQueryColumn('Id', 'Id')];
    const oPath = "ParentId = '" + localStorage.getItem('profileId') + "' AND Designation = 'Favoriten'";
    return this.dataService.readObjects(ObjectKeys.BOX, queryColumns, oPath);
  }

  public deleteFavoriten(id: string) {
    const objectKey: IPersistable = {
      id: id,
      objectType: ObjectKeys.BOXDETAIL,
      persistenceCategory: null,
      changesPersisted: (persistMode: PersistMode) => (persistMode = PersistMode.Delete),
      revertChanges: () => {},
    };

    this.persistenceService.clearPendingObjects();
    this.persistenceService.addObjectForDelete(objectKey);
    this.allFavorites?.deleteFavoriteFromList(id);
    this.persistenceService.persistPendingChanges();
  }

  private persistBoxFavorite(boxIdFavoriten: string): string {
    const objectKey: IPersistable = {
      id: boxIdFavoriten,
      objectType: ObjectKeys.BOX,
      changesPersisted: (persistMode) => (persistMode = PersistMode.Insert),
      revertChanges: () => {},
    };
    this.persistenceService.clearPendingObjects();
    this.persistenceService.addObjectForInsert(objectKey);
    this.persistenceService.addObjectValue(objectKey, 'Designation', 'Favoriten');
    this.persistenceService.addObjectValue(objectKey, 'ParentId', localStorage.getItem('profileId'));
    this.persistenceService.addObjectValue(objectKey, 'Number', 1);
    this.persistenceService.addObjectValue(objectKey, 'Type', '0');
    this.persistenceService.persistPendingChanges();
    return boxIdFavoriten;
  }

  public persistFavoriten(
    id: string,
    parentObjectType: string | number,
    parentId: string,
    designation: string
  ): Observable<BoxDetailViewModel> {
    const objectKey: IPersistable = {
      id: id,
      objectType: ObjectKeys.BOXDETAIL,
      changesPersisted: (persistMode) => (persistMode = PersistMode.Insert),
      revertChanges: () => {},
    };

    this.persistenceService.addObjectForInsert(objectKey);
    this.persistenceService.addObjectValue(objectKey, 'ParentObjectType', parentObjectType);
    this.persistenceService.addObjectValue(objectKey, 'ParentId', parentId);

    if (this.boxIdFavoriten) {
      this.persistenceService.addObjectValue(objectKey, 'gebId', this.boxIdFavoriten);
      this.persistenceService.persistPendingChanges();
    } else {
      this.getBoxId().subscribe((data: string) => {
        this.boxIdFavoriten = data;
        this.persistenceService.addObjectValue(objectKey, 'gebId', data);
        this.persistenceService.persistPendingChanges();
      });
    }
    return this.persistenceService.afterPersistPendingChanges$.pipe(first()).pipe(
      map(() => {
        const BoxDetail = {
          Id: id,
          ParentId: parentId,
          ParentObjectType: parentObjectType,
          gebId: this.boxIdFavoriten,
          DisplayText: designation,
        };
        this.allFavorites?.favorites?.set(id, new BoxDetailViewModel(BoxDetail));
        return new BoxDetailViewModel(BoxDetail);
      })
    );
  }
  
  public addFavorite(e: ColumnButtonClickEvent,type: ObjectTypeNames, items: ItemsArray,clonedItems?:Address[]) : void{
    if(e.row.data.IsFavorite === 1) {
      return;
    } else {
      e.row.data.IsFavorite = 1;
      this
        .persistFavoriten(GUID.newGUID(), type, e.row?.data.Id, (type=== ObjectTypeNames.Company) ? e.row?.data.Name: e.row?.data.Designation)
        .pipe(first())
        .subscribe({
        next: (data) => {
          this.updateItem(e, FavoriteAction.ADD,type, items,data,clonedItems);
        },
        error: () => {
          e.row.data.IsFavorite = 0;
        }
      });
    }
  }
  
  public updateItem(e: ColumnButtonClickEvent, action: FavoriteAction,type: ObjectTypeNames,items: ItemsArray, favoriteData?: BoxDetailViewModel,clonedItems?:Address[]) : void {
    let selectedIndex = items.findIndex((audit) => audit.Id === e.row?.data.Id);
    const typeMap = {
      [ObjectTypeNames.AudAudit]: Audit,
      [ObjectTypeNames.Company]: Address,
      [ObjectTypeNames.AudLibrary]: Library,
      [ObjectTypeNames.AudBibCategory]: BibCategory,
      [ObjectTypeNames.AudBibQuestion]: BibQuestion,
      [ObjectTypeNames.AudBibResponse]: BibResponse,
      [ObjectTypeNames.AudBibMeasure]: BibMeasure
    };
    const item= new typeMap[type](null);
    items[selectedIndex]=Object.assign(item,e.row?.data,action === FavoriteAction.ADD ? { IsFavorite: 1, BoxDetailId: favoriteData.Id } : { IsFavorite: 0 });
    if(clonedItems) {clonedItems[selectedIndex]=Object.assign(item,e.row?.data,action === FavoriteAction.ADD ? { IsFavorite: 1, BoxDetailId: favoriteData.Id } : { IsFavorite: 0 });};
  }

  public isFavoriteVisible(e : {component: dxDataGrid<Items,string>,row?: Row<Items,string>,column?: Column<Items,string>}) : boolean{
    if (e.row?.data['IsFavorite'] === 1) {
      return true;
    } else {
      return false;
    }
  }
}
